import React, { PureComponent } from 'react';
import { I, webRoot } from '../../../../common/v5/config';
import { SerialMultiSelect , SingleSelectDD } from '../../../../reactcomponents/Dropdown';
import HelpTooltip from './HelpTooltip';
import { OBJ_ID_NAME } from '../../../../common/v5/constants';
import './AgentForm.css';

const toAreaIds = (arr) =>
  (arr || [])
    .map((id) => (typeof id === 'number' ? id : parseInt(id, 10)))
    .filter((n) => !isNaN(n));

function getAutoSendTagLevelIds(meta) {
  const v = meta?.auto_send_tag_level_ids;
  if (!v) return [];
  if (Array.isArray(v)) return v.map(Number).filter(n => !isNaN(n));
  return String(v).split(',').map(s => parseInt(s.trim(), 10)).filter(n => !isNaN(n));
}

// Areas: (1) form/modal state from parent, (2) API list shape (area_ids). No single-area fallback; list is normalized in loadAgents.
function getAreasFromAgent(agent) {
  if (!agent) return [];
  if (agent.areas != null && Array.isArray(agent.areas)) return toAreaIds(agent.areas);
  if (agent.area_ids != null && Array.isArray(agent.area_ids)) return toAreaIds(agent.area_ids);
  return [];
}

// Flatten hierarchical tag list from fetchAreaTags into [{Id, Name}] for SerialMultiSelect.
// Each tag has { id: TagLevelId, value: "name", children: [...] }.
function flattenTagsForDropdown(tags, prefix) {
  const out = [];
  if (!Array.isArray(tags)) return out;
  for (const t of tags) {
    const label = prefix ? `${prefix} / ${t.value}` : t.value;
    out.push({ Id: t.id, Name: label });
    if (t.children && t.children.length > 0) {
      out.push(...flattenTagsForDropdown(t.children, label));
    }
  }
  return out;
}

class AgentForm extends PureComponent {
  constructor(props) {
    super(props);

    // Initialize state with agent data if editing
    const { agent } = props;
    const areas = getAreasFromAgent(agent);
    console.log('[AgentForm:Areas] constructor', { agentId: agent?.agent_id ?? agent?.id, areas, agentAreaIds: agent?.area_ids, agentAreaId: agent?.area_id });

    const agentMeta = agent?.meta || {};
    this.state = {
      formData: {
        name: agent?.name || agent?.title || '',
        description: agent?.description || '',
        agent_type: this.mapAgentTypeFromAPI(agent?.agent_type || agent?.type) || 'AUTO_ANSWER',
        areas: areas,
        user_id: agent?.user_id || '',
        parentAgentId: agent?.parent_agent_id || agent?.parentAgentId || '',
        showUser: false,
        use_context: agentMeta.use_context !== false,
        kb_source_ids: agentMeta.kb_source_ids || [],
        auto_send_tag_level_ids: getAutoSendTagLevelIds(agentMeta),
      },
      showAreasDropdown: false,
      showKbSourcesDropdown: false,
      showTagsDropdown: false,
      availableTags: [],
      tagsLoading: false,
    };

    // Bind methods
    this.handleInputChange = this.handleInputChange.bind(this);
    this.handleAreasSelect = this.handleAreasSelect.bind(this);
    this.handleTypeChange = this.handleTypeChange.bind(this);
    this.handleSelectUser = this.handleSelectUser.bind(this);
    this.handleToggleUser = this.handleToggleUser.bind(this);
    this.handleKbSourcesSelect = this.handleKbSourcesSelect.bind(this);
    this.handleTagsSelect = this.handleTagsSelect.bind(this);
  }

  // Map API agent types to UI format
  mapAgentTypeFromAPI(apiType) {
    switch (apiType) {
      case 'router':
        return 'ROUTER';
      case 'auto-answer':
        return 'AUTO_ANSWER';
      case 'chat':
        return 'CHAT';
      case 'team-member':
        return 'TEAM_MEMBER';
      case 'supervisor':
        return 'SUPERVISOR';
      case 'agent-assist':
        return 'AGENT_ASSIST';
      default:
        return 'AUTO_ANSWER';
    }
  }

  componentDidMount() {
    const areas = this.state.formData.areas;
    if (areas.length > 0) {
      if (this.props.loadKnowledgeBaseSources) {
        this.props.loadKnowledgeBaseSources(areas);
      }
      this._fetchTagsForAreas(areas);
    }
  }

  componentDidUpdate(prevProps, prevState) {
    // Update form data when agent prop changes
    if (prevProps.agent !== this.props.agent) {
      const { agent } = this.props;
      const areas = getAreasFromAgent(agent);
      const updatedMeta = agent?.meta || {};
      const formData = {
        name: agent?.name || agent?.agent_name || agent?.title || '',
        description: agent?.description || '',
        agent_type: this.mapAgentTypeFromAPI(agent?.agent_type || agent?.type) || '',
        areas: areas,
        user_id: agent?.user_id || '',
        parentAgentId: agent?.parent_agent_id || agent?.parentAgentId || '',
        use_context: updatedMeta.use_context !== false,
        kb_source_ids: updatedMeta.kb_source_ids || [],
        auto_send_tag_level_ids: getAutoSendTagLevelIds(updatedMeta),
      };
      console.log('[AgentForm:Areas] componentDidUpdate agent changed', { areas, formDataAreas: formData.areas });
      this.setState({ formData });
    }

    // Reload KB sources and tags when selected areas change
    const prevAreas = prevState?.formData?.areas || [];
    const currAreas = this.state.formData.areas || [];
    const areasChanged = prevAreas.join(',') !== currAreas.join(',');
    if (areasChanged) {
      if (this.props.loadKnowledgeBaseSources) {
        this.props.loadKnowledgeBaseSources(currAreas.length > 0 ? currAreas : []);
      }
      if (currAreas.length > 0) {
        this._fetchTagsForAreas(currAreas);
      } else {
        this.setState({ availableTags: [] });
      }
    }
  }

  _fetchTagsForAreas(areaIds) {
    if (!areaIds || areaIds.length === 0) return;
    this.setState({ tagsLoading: true });
    const url = webRoot + 'errand/fetchAreaTags';
    fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      body: 'areas=' + areaIds.join(','),
      credentials: 'same-origin',
    })
      .then(r => r.json())
      .then(envelope => {
        // Unwrap MCAM envelope: { mcam: { channels: [{ content: "JSON string" }] } }
        let tagData = {};
        try {
          const channels = envelope?.mcam?.channels || [];
          for (const ch of channels) {
            if (ch.id === 'FetchAreaTags' && ch.content) {
              tagData = typeof ch.content === 'string'
                ? JSON.parse(ch.content)
                : ch.content;
              break;
            }
          }
        } catch (e) {
          console.error('[AgentForm] Failed to parse fetchAreaTags MCAM response', e);
        }

        let allTags = [];
        for (const areaId of Object.keys(tagData)) {
          const areaTags = tagData[areaId];
          if (areaTags && areaTags.normal_tags) {
            allTags = allTags.concat(flattenTagsForDropdown(areaTags.normal_tags, ''));
          }
        }
        const seen = new Set();
        const unique = [];
        for (const t of allTags) {
          if (!seen.has(t.Id)) {
            seen.add(t.Id);
            unique.push(t);
          }
        }
        unique.sort((a, b) => (a.Name || '').localeCompare(b.Name || ''));
        this.setState({ availableTags: unique, tagsLoading: false });
      })
      .catch(() => {
        this.setState({ availableTags: [], tagsLoading: false });
      });
  }

  // Notify parent of changes
  notifyChange() {
    const { onChange } = this.props;
    if (onChange) {
      const { formData } = this.state;
      onChange(formData);
    }
  }

  handleInputChange(field, value) {
    this.setState(prevState => ({
      formData: {
        ...prevState.formData,
        [field]: value
      }
    }), () => {
      this.notifyChange();
    });
  }

  // SerialMultiSelect onSelect passes comma-separated string; normalize to array of numbers and dedupe
  handleAreasSelect(selectedStr) {
    console.log('[AgentForm:Areas] handleAreasSelect called', { selectedStr, type: typeof selectedStr });
    const ids = (selectedStr == null || selectedStr === '' ? [] : String(selectedStr).split(','))
      .map((s) => parseInt(s.trim(), 10))
      .filter((n) => !isNaN(n));
    const deduped = [...new Set(ids)];
    console.log('[AgentForm:Areas] handleAreasSelect parsed', { ids, deduped });
    this.handleInputChange('areas', deduped);
  }

  handleTagsSelect(selectedStr) {
    const ids = (selectedStr == null || selectedStr === '' ? [] : String(selectedStr).split(','))
      .map(s => parseInt(s.trim(), 10))
      .filter(n => !isNaN(n));
    this.handleInputChange('auto_send_tag_level_ids', [...new Set(ids)]);
  }

  handleSelectUser(userId) {
    this.handleInputChange('user_id', userId);
  }

  handleToggleUser() {
    this.setState(prevState => ({
      showUser: !prevState.showUser
    }));
  }

  _getKbSourcesDropdownData() {
    const { kbSources = [] } = this.props;
    const faqItems = [];
    const docItems = [];
    for (const src of kbSources) {
      const item = { Id: src.source_id, Name: src.name };
      if (src.type === 'library_question') {
        faqItems.push(item);
      } else {
        docItems.push(item);
      }
    }
    faqItems.sort((a, b) => (a.Name || '').localeCompare(b.Name || ''));
    docItems.sort((a, b) => (a.Name || '').localeCompare(b.Name || ''));

    const groups = [];
    if (faqItems.length > 0) {
      groups.push({ Id: 'grp_faq', Name: I('FAQ Entries'), Items: faqItems });
    }
    if (docItems.length > 0) {
      groups.push({ Id: 'grp_doc', Name: I('Documents'), Items: docItems });
    }
    return groups;
  }

  _getKbSourcesSelectedStr() {
    const ids = this.state.formData.kb_source_ids || [];
    return ids.length > 0 ? ids.join(',') : '';
  }

  _getKbSourcesHeaderText() {
    const ids = this.state.formData.kb_source_ids || [];
    if (ids.length === 0) return null;
    const { kbSources = [] } = this.props;
    if (ids.length === kbSources.length && kbSources.length > 0) return null;
    if (ids.length === 1) {
      const match = kbSources.find(s => s.source_id === ids[0]);
      return match ? match.name : null;
    }
    return `${ids.length} ${I('sources selected')}`;
  }

  _renderKbSourcesPicker(areasArray) {
    const { kbSources = [], kbSourcesLoading = false } = this.props;

    if (areasArray.length === 0) {
      return (
        <div className="kb-status-message">
          {I('Select area(s) first to see available knowledge base sources.')}
        </div>
      );
    }

    if (kbSourcesLoading) {
      return (
        <div className="kb-status-message">
          <i className="fa fa-spinner fa-spin" style={{ marginRight: '6px' }} aria-hidden="true" />
          {I('Loading knowledge base sources...')}
        </div>
      );
    }

    if (kbSources.length === 0) {
      return (
        <div className="kb-status-message">
          {I('No knowledge base documents found for the selected area(s).')}
          <br />
          <span className="kb-status-hint">
            {I('Upload documents or add FAQ entries to use agent-scoped filtering.')}
          </span>
        </div>
      );
    }

    const headerText = this._getKbSourcesHeaderText();
    const kbSelectProps = {
      id: 'agent-kb-sources',
      flip: false,
      title: I('All sources (no filter)'),
      className: 'popup-multi-select admin-text-input-qrm',
      overrideTitle: true,
      multiSelect: true,
      groupSelect: true,
      nested: { key: 'Items' },
      data: this._getKbSourcesDropdownData(),
      idFields: OBJ_ID_NAME,
      selected: this._getKbSourcesSelectedStr(),
      onSelect: this.handleKbSourcesSelect,
      onToggle: () => this.setState(s => ({ showKbSourcesDropdown: !s.showKbSourcesDropdown })),
      show: this.state.showKbSourcesDropdown,
      selectAll: true,
      selectNone: true,
      enableSearch: true,
    };
    if (headerText) {
      kbSelectProps.staticHeaderText = headerText;
    }

    return (
      <>
        <SerialMultiSelect {...kbSelectProps} />
        <div className="kb-help-text">
          {I('Leave empty to use all context documents in the area.')}
        </div>
      </>
    );
  }

  handleKbSourcesSelect(selectedStr) {
    const ids = (selectedStr == null || selectedStr === '' ? [] : String(selectedStr).split(','))
      .map(s => parseInt(s.trim(), 10))
      .filter(n => !isNaN(n));
    const deduped = [...new Set(ids)];
    this.handleInputChange('kb_source_ids', deduped);
  }

  handleTypeChange(e) {
    const newType = e.target.value;
    this.setState(prevState => ({
      formData: {
        ...prevState.formData,
        agent_type: newType,
        parentAgentId: newType === 'ROUTER' ? '' : prevState.formData.parentAgentId
      }
    }), () => {
      this.notifyChange();
    });
  }

  _getSelectedTagsStr() {
    const ids = this.state.formData.auto_send_tag_level_ids || [];
    return ids.length > 0 ? ids.join(',') : '';
  }

  _getTagsHeaderText() {
    const ids = this.state.formData.auto_send_tag_level_ids || [];
    if (ids.length === 0) return null;
    const { availableTags } = this.state;
    if (ids.length === 1) {
      const match = availableTags.find(t => t.Id === ids[0]);
      return match ? match.Name : null;
    }
    return `${ids.length} ${I('tags selected')}`;
  }

  render() {
    const { areaList = [], agent } = this.props;
    const { formData, availableTags, tagsLoading } = this.state;
    const areasArray = Array.isArray(formData.areas) ? formData.areas : [];
    const selectedStr = areasArray.length > 0 ? areasArray.join(',') : '';
    const dataArray = Array.isArray(areaList) ? areaList : [];
    if (this.state.showAreasDropdown || areasArray.length > 0) {
      console.log('[AgentForm:Areas] render', {
        areaListIsArray: Array.isArray(areaList),
        areaListLength: dataArray.length,
        formDataAreas: formData.areas,
        selectedStr,
        showDropdown: this.state.showAreasDropdown
      });
    }
    // Only disable name when editing an existing agent (non-zero id). Create New Agent has id: 0.
    const isEditing = !!(
      agent &&
      ((agent.id != null && agent.id !== 0 && agent.id !== '0') ||
        (agent.agent_id != null && agent.agent_id !== 0 && agent.agent_id !== '0'))
    );

    const tagsSelectedStr = this._getSelectedTagsStr();
    const tagsHeaderText = this._getTagsHeaderText();

    const tagSelectProps = {
      id: 'agent-auto-send-tags',
      flip: false,
      title: I('Select reporting tag(s)...'),
      className: 'popup-multi-select admin-text-input-qrm',
      overrideTitle: true,
      multiSelect: true,
      groupSelect: false,
      data: availableTags,
      idFields: OBJ_ID_NAME,
      selected: tagsSelectedStr,
      onSelect: this.handleTagsSelect,
      onToggle: () => this.setState(s => ({ showTagsDropdown: !s.showTagsDropdown })),
      show: this.state.showTagsDropdown,
      selectAll: true,
      selectNone: true,
      enableSearch: true,
    };
    if (tagsHeaderText) {
      tagSelectProps.staticHeaderText = tagsHeaderText;
    }

    return (
      <div className="admin-one-form custom">
        <div className="agent-manager">
          {/* Agent Name */}
          <div className="form-input-with-label-row">
            <div className="form-label mandatory">
              {I("Agent Name")}
            </div>
            <div className="form-input-container">
              <input
                type="text"
                id="agentName"
                name="agentName"
                value={formData.name}
                onChange={(e) => this.handleInputChange('name', e.target.value)}
                className="admin-text-input"
                placeholder={I("Enter agent name")}
                disabled={isEditing}
                style={isEditing ? { backgroundColor: '#f5f5f5', color: '#999', cursor: 'not-allowed' } : {}}
              />
            </div>
          </div>

          {/* Agent Description */}
          <div className="form-input-with-label-row">
            <div className="form-label mandatory">
              {I("Agent Description")}
            </div>
            <div className="form-input-container">
              <textarea
                id="agentDescription"
                name="agentDescription"
                value={formData.description}
                onChange={(e) => this.handleInputChange('description', e.target.value)}
                className="admin-text-input"
                rows={4}
                placeholder={I("Enter agent description")}
              />
            </div>
          </div>

          {/* Agent Type */}
          <div className="form-input-with-label-row">
            <div className="form-label mandatory">
              {I("Agent Type")}
              <HelpTooltip
                content={`<strong>${I("Default:")}</strong> ${I("Standard agent type")}<br/><strong>${I("Router:")}</strong> ${I("Routes conversations to appropriate agents")}`}
                position="top"
              />
            </div>
            <div className="form-input-container">
              <select
                id="agentType"
                value={formData.agent_type}
                onChange={this.handleTypeChange}
                className="form-control styled-select"
              >
                <option value="">{I('Default')}</option>
                <option value="ROUTER">{I('Router')}</option>
              </select>
            </div>
          </div>

          {/* Areas (multi-select) */}
          <div className="form-input-with-label-row">
            <div className="form-label mandatory">
              {I("Areas")}
              <HelpTooltip
                content={I("Select one or more areas this agent belongs to. This determines access permissions and routing rules.")}
                position="top"
              />
            </div>
            <div className="form-input-container">
              <SerialMultiSelect
                id="agent-areas"
                flip={false}
                title={I('Select area(s)...')}
                className="popup-multi-select admin-text-input-qrm"
                overrideTitle={true}
                multiSelect={true}
                groupSelect={true}
                nested={{ key: 'Areas' }}
                data={dataArray}
                idFields={OBJ_ID_NAME}
                selected={selectedStr}
                onSelect={this.handleAreasSelect}
                onToggle={() => {
                  const next = !this.state.showAreasDropdown;
                  console.log('[AgentForm:Areas] onToggle', { next, currentAreas: formData.areas });
                  this.setState((s) => ({ showAreasDropdown: !s.showAreasDropdown }));
                }}
                show={this.state.showAreasDropdown}
                selectAll={true}
                selectNone={true}
                enableSearch={true}
              />
            </div>
          </div>

          {/* Parent Agent Selection */}
          <div className="form-input-with-label-row">
            <div className="form-label">
              {I("Parent Agent")} <span className="optional-text">({I("Optional")})</span>
              <HelpTooltip
                content={formData.agent_type === 'ROUTER'
                  ? I("Router agents cannot have a parent. They must be at the top level of the hierarchy.")
                  : I("Select a parent agent to create a hierarchical structure. Leave empty for root agents.")
                }
                position="top"
              />
            </div>
            <div className="form-input-container">
              <select
                id="parentAgent"
                value={formData.parentAgentId || ''}
                onChange={(e) => this.handleInputChange('parentAgentId', e.target.value)}
                className="form-control styled-select"
                disabled={formData.agent_type === 'ROUTER'}
              >
                <option value="">{I('No parent (root agent)')}</option>
                {this.props.agents && this.props.agents.filter(agent =>
                  !this.props.agent || (agent.id || agent.agent_id) !== (this.props.agent.id || this.props.agent.agent_id)
                ).map((agent) => (
                  <option key={agent.id || agent.agent_id} value={agent.agent_id || agent.id}>
                    {agent.name || agent.title || agent.agent_name || agent.agent_id} - {agent.description}
                  </option>
                ))}
              </select>
              {formData.agent_type === 'ROUTER' && (
                <div className="form-help-text" style={{ marginTop: '5px', fontSize: '12px', color: '#666' }}>
                  {I('Router agents are automatically set as root agents (no parent).')}
                </div>
              )}
            </div>
          </div>

          {/* User ID */}
          <div className="form-input-with-label-row">
            <div className="form-label">
              {I("User ID")}
              <HelpTooltip
                content={I("Specify the user ID linked to this agent.")}
                position="top"
              />
            </div>
            <div className="form-input-container">
              <SingleSelectDD
                id={"user_id"}
                className="popup-multi-select"
                overrideTitle={true}
                groupSelect={false}
                data={this.props.agentList}
                idFields={OBJ_ID_NAME}
                selected={formData.user_id}
                onSelect={this.handleSelectUser}
                onToggle={this.handleToggleUser}
                show={this.state.showUser}
                selectAll={false}
                selectNone={false}
              />
            </div>
          </div>

          {/* Knowledge Base Section */}
          <div className="kb-section">
            <div className="kb-section-header">{I("Knowledge Base")}</div>

            <div className="form-input-with-label-row">
              <div className="form-label">
                {I("Use Context")}
                <HelpTooltip
                  content={I("Enable context retrieval from knowledge base documents for this agent. When disabled, the agent will not use any uploaded context.")}
                  position="top"
                />
              </div>
              <div className="form-input-container">
                <label className="kb-toggle-label">
                  <input
                    type="checkbox"
                    checked={formData.use_context}
                    onChange={(e) => this.handleInputChange('use_context', e.target.checked)}
                  />
                  {formData.use_context ? I('Enabled') : I('Disabled')}
                </label>
              </div>
            </div>

            {formData.use_context && (
              <div className="form-input-with-label-row">
                <div className="form-label">
                  {I("Sources")} <span className="optional-text">({I("Optional")})</span>
                  <HelpTooltip
                    content={I("Restrict this agent to specific knowledge base sources. Leave empty to use all available context in the area.")}
                    position="top"
                  />
                </div>
                <div className="form-input-container">
                  {this._renderKbSourcesPicker(areasArray)}
                </div>
              </div>
            )}
          </div>

          {/* Auto-send reporting tags */}
          <div className="kb-section">
            <div className="kb-section-header">{I('Auto-send answer')}</div>
            <div className="form-input-with-label-row">
              <div className="form-label">
                {I('Reporting tags')} <span className="optional-text">({I('Optional')})</span>
                <HelpTooltip
                  content={I('Tags applied to errands when this agent sends an automatic answer. Works the same as manual reply tagging for reporting purposes.')}
                  position="top"
                />
              </div>
              <div className="form-input-container">
                {areasArray.length === 0 ? (
                  <div style={{ fontSize: '13px', color: '#888', padding: '6px 0' }}>
                    {I('Select an area first to see available tags.')}
                  </div>
                ) : tagsLoading ? (
                  <div style={{ fontSize: '13px', color: '#888', padding: '6px 0' }}>
                    <i className="fa fa-spinner fa-spin" /> {I('Loading tags...')}
                  </div>
                ) : availableTags.length === 0 ? (
                  <div style={{ fontSize: '13px', color: '#888', padding: '6px 0' }}>
                    {I('No tags configured for the selected area(s).')}
                  </div>
                ) : (
                  <SerialMultiSelect {...tagSelectProps} />
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }
}

export default AgentForm;
